// /bootcampy — seznam lokalit bootcampu (22.8.2026, přepsáno 16.9.2026).
//
// 16.9.2026: běh se stal LOKALITOU. Cvičení je celoroční, nekončí sezónou,
// takže řádek v bootcampy není "běh od-do", ale místo, kam se chodí. Prázdné
// datum do znamená bez konce. Adresa a poznámka patří k místu a bydlí ve
// stejném řádku, samostatná tabulka lokalit byla slepá ulička.
//
// Stavěné podle stránky Klienti: gradientní hlavička, karta se seznamem,
// hledání a filtry v hlavičce tabulky. Detail se otevírá jako překryv stejně
// jako detail klienta, a dá se přehodit na celou stránku (?beh=ID), kde se
// chová jako záložka adminu, přesně jako úkoly.
//
// Účastnice bootcampu nejsou v clients, mají vlastní tabulky (netlify/functions/
// _lib/bootcampLib.cjs). Do sekce Klienti tedy nepatří a bydlí tady.

import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Flame, Plus, CalendarDays, MapPin, Search, X, RefreshCw, Save, ChevronRight, Infinity as Nekonecno,
  Archive, ArchiveRestore, Eye, EyeOff, PauseCircle,
} from "lucide-react";
import PoleDatumDo from "../../components/PoleDatumDo.jsx";
import { RozvrhEditor, MistaEditor, PauzyEditor } from "../../components/BootcampNastaveni.jsx";
import Card from "../../components/Card.jsx";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, ErrorNote } from "../../components/ui.jsx";
import MultiSelectFilter from "../../components/MultiSelectFilter.jsx";
import InfoTip from "../../components/InfoTip.jsx";
import { useApiData, apiFetch } from "../../lib/api.js";
import { getCurrentUser } from "../../lib/auth.js";
import { formatDate as fmtDate } from "../../lib/format.js";
import BootcampDetail from "./Detail.jsx";

const STAV_META = {
  pripravovany: { label: "Připravovaná", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  probiha:      { label: "V provozu",    cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  ukonceny:     { label: "Ukončená",     cls: "bg-slate-100 text-slate-500 border-slate-200" },
  // 3. 11. 2026: lokalita, která se zatím nespouští (Letná). Bez termínů a zpráv.
  nespusteno:   { label: "Zatím nespuštěno", kratky: "Nespuštěno", cls: "bg-amber-50 text-amber-800 border-amber-200", Ikona: PauseCircle },
};

function den(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : fmtDate(d);
}
function bezDiakritiky(s) {
  return String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}
function StavBadge({ stav }) {
  const m = STAV_META[stav] || STAV_META.pripravovany;
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap text-[12.5px] font-medium px-2.5 py-1 rounded-full border ${m.cls}`}>
      {m.Ikona && <m.Ikona className="w-3.5 h-3.5 shrink-0" />}
      {m.kratky ? <><span className="sm:hidden">{m.kratky}</span><span className="hidden sm:inline">{m.label}</span></> : m.label}
    </span>
  );
}
function Pole({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-slate-500 mt-1">{hint}</span>}
    </label>
  );
}

// Předvyplněno podle skutečných položek na FAPI fakturách (účet Tinka):
//   "Bootcamp: Na zkoušku - 4 lekce"    1 880 Kč
//   "Bootcamp: Po svém - 8 lekcí"       3 450 Kč
//   "Bootcamp: Naplno - bez omezení"    6 600 Kč
// Vzor "Bootcamp:" s dvojtečkou schválně: staré faktury "Letní Bootcamps 8-26"
// dvojtečku nemají, takže se do nového běhu nikdy nepřipletou.
const PRAZDNY = {
  nazev: "", adresa: "", poznamka: "", trener_id: "", datum_od: "", datum_do: "", bez_konce: true,
  prihlasky_do: "", prodej_od: "",
  cas_od: "18:00", cas_do: "19:00", kapacita: 14, storno_hodin: 24,
  prihlaseni_do: "10:00", prihlaseni_dnu_predem: 0,
  rozvrh: [{ den: 2, cas_od: "18:00", cas_do: "19:00" }, { den: 4, cas_od: "18:00", cas_do: "19:00" }],
  mista: [], pauzy: [], fapi_form_id: "", telegram_chat_id: "", platnost_dnu: 60,
  // Stav se nenastavuje ručně, plyne z dat (viz bootcampLib.stavBehu).
  cena_text: "1 880 / 3 450 / 6 600 Kč", fapi_pattern: "Bootcamp:", popis: "", ukoncen_rucne: false, nespusteno: false,
  tarify: [
    { nazev: "Na zkoušku", vzor: "na zkousku", vstupy: 4, cena: 1880 },
    { nazev: "Po svém", vzor: "po svem", vstupy: 8, cena: 3450 },
    { nazev: "Naplno", vzor: "naplno", vstupy: "", cena: 6600 },
  ],
};

// Platby u lokality: zaplaceno, po splatnosti, čeká na úhradu. Nula se
// neukazuje, takže u lokality, kde mají všechny zaplaceno, zůstane jen fajfka.
function Platby({ zaplaceno = 0, celkem = 0, poSplatnosti = 0 }) {
  const ceka = Math.max(0, Number(celkem) - Number(zaplaceno) - Number(poSplatnosti));
  const kusy = [
    { znak: "\u2705", pocet: Number(zaplaceno), popis: "uhrazeno", trida: "text-emerald-700" },
    { znak: "\u274C", pocet: Number(poSplatnosti), popis: "po splatnosti", trida: "text-rose-700" },
    { znak: "\u23F3", pocet: ceka, popis: "čeká na úhradu", trida: "text-amber-700" },
  ].filter((k) => k.pocet > 0);
  if (!kusy.length) return <span className="text-slate-300">—</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
      {kusy.map((k) => (
        <span key={k.popis} title={`${k.pocet} ${k.popis} z ${celkem} přihlášených`}
          className={`inline-flex items-center gap-1 ${k.trida}`}>
          <span aria-hidden="true">{k.znak}</span>
          <b className="tabular-nums">{k.pocet}</b>
          <span className="sr-only">{k.popis}</span>
        </span>
      ))}
    </span>
  );
}

// ── Nabídka lokalit na webu ─────────────────────────────────────────────────
// Prodejní stránka fitway40.cz je statická, vygenerovaná dopředu. Přepnutí se
// proto uloží do databáze a rovnou spustí přestavbu webu, která je obvykle
// hotová do dvou minut. Do té doby web ukazuje původní stav, proto to tlačítko
// po přepnutí říká, že se přestavba rozjela (8. 10. 2026).
function NabidkaNaWebu({ behy, hookNastaven, onZmena }) {
  const [busy, setBusy] = useState("");
  const [hlaska, setHlaska] = useState("");
  const [chyba, setChyba] = useState("");
  const [hook, setHook] = useState("");

  const zive = behy.filter((b) => !b.archivovana);

  const prepni = async (b) => {
    const skryt = !b.skryta_na_webu;
    setBusy(String(b.id)); setChyba(""); setHlaska("");
    try {
      const r = await apiFetch("/api/bootcampy", {
        method: "POST",
        body: JSON.stringify({ akce: "skryt-na-webu", id: b.id, skryta: skryt }),
      });
      setHlaska(r?.prestavba?.spustena
        ? `${b.nazev}: ${skryt ? "skrytá" : "nabízí se"}. Web se přestavuje, za pár minut to bude venku.`
        : `${b.nazev}: ${skryt ? "skrytá" : "nabízí se"}. Přestavba webu se ale nespustila (${r?.prestavba?.duvod || "neznámý důvod"}), web zatím ukazuje původní stav.`);
      if (onZmena) onZmena();
    } catch (e) {
      setChyba(e.message || "Přepnutí se nepovedlo.");
    } finally {
      setBusy("");
    }
  };

  const ulozHook = async () => {
    setBusy("hook"); setChyba(""); setHlaska("");
    try {
      await apiFetch("/api/bootcampy", { method: "POST", body: JSON.stringify({ akce: "ulozit-web-hook", url: hook }) });
      setHlaska("Adresa přestavby uložena.");
      setHook("");
      if (onZmena) onZmena();
    } catch (e) {
      setChyba(e.message || "Uložení se nepovedlo.");
    } finally {
      setBusy("");
    }
  };

  const prestavet = async () => {
    setBusy("build"); setChyba(""); setHlaska("");
    try {
      const r = await apiFetch("/api/bootcampy", { method: "POST", body: JSON.stringify({ akce: "prestavet-web" }) });
      setHlaska(r?.prestavba?.spustena ? "Web se přestavuje." : `Přestavba se nespustila: ${r?.prestavba?.duvod || "neznámý důvod"}.`);
    } catch (e) {
      setChyba(e.message || "Přestavba se nepovedla.");
    } finally {
      setBusy("");
    }
  };

  return (
    <Card lista title="Nabídka na webu">
      <p className="text-sm text-slate-600 mb-3">
        Která lokalita se nabízí na prodejní stránce a v pokladně. Skrytá lokalita
        v adminu zůstává i s klientkami a lekcemi, jen se na webu neprodává.
        Změna se na webu objeví asi do dvou minut, než se stránka přestaví.
      </p>

      <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
        {zive.map((b) => (
          <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 bg-white">
            <div>
              <div className="font-semibold text-slate-900">{b.nazev}</div>
              <div className="text-xs text-slate-500">
                {b.nespusteno
                  ? "Na webu se nenabízí, dokud lokalitu nespustíš"
                  : (b.skryta_na_webu ? "Na webu se nenabízí" : "Na webu se nabízí")}
              </div>
            </div>
            <button
              type="button"
              disabled={busy === String(b.id)}
              onClick={() => prepni(b)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl border transition-colors min-h-[40px] disabled:opacity-60 ${
                b.skryta_na_webu
                  ? "bg-emerald-50 hover:bg-emerald-100 border-emerald-200 text-emerald-700"
                  : "bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-700"
              }`}
            >
              {b.skryta_na_webu
                ? <><Eye className="w-4 h-4" /> Začít nabízet</>
                : <><EyeOff className="w-4 h-4" /> Skrýt na webu</>}
            </button>
          </div>
        ))}
        {zive.length === 0 && <div className="px-3 py-4 text-sm text-slate-500">Žádná aktivní lokalita.</div>}
      </div>

      {hlaska && <div className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">{hlaska}</div>}
      {chyba && <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">{chyba}</div>}

      {!hookNastaven && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3">
          <div className="text-sm font-medium text-amber-900">Chybí adresa pro přestavbu webu</div>
          <p className="text-xs text-amber-800 mt-1">
            Bez ní se přepnutí uloží, ale web se sám nepřestaví. Adresu najdeš v Netlify
            u projektu fitway40 v Project configuration, Developer settings, Build hooks.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <input
              className="field flex-1 min-w-[260px]"
              value={hook}
              onChange={(e) => setHook(e.target.value)}
              placeholder="https://api.netlify.com/build_hooks/..."
            />
            <button type="button" disabled={busy === "hook" || !hook.trim()} onClick={ulozHook}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl bg-brand text-white hover:opacity-90 disabled:opacity-50 min-h-[40px]">
              <Save className="w-4 h-4" /> Uložit
            </button>
          </div>
        </div>
      )}

      {hookNastaven && (
        <div className="mt-3">
          <button type="button" disabled={busy === "build"} onClick={prestavet}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 min-h-[40px] disabled:opacity-60">
            <RefreshCw className="w-4 h-4" /> Přestavět web teď
          </button>
        </div>
      )}
    </Card>
  );
}

export default function BootcampyPrehled() {
  const [searchParams, setSearchParams] = useSearchParams();
  const behParam = searchParams.get("beh");

  // Archiv se schvalne nenacita rovnou. Archivovana lokalita uz nejede a
  // v beznem seznamu by jen prekazela (16.9.2026).
  const [sArchivem, setSArchivem] = useState(false);
  const { data, loading, refetch, refreshing } = useApiData(`/api/bootcampy${sArchivem ? "?archiv=1" : ""}`);
  // 16.9.2026: bez filtru na trenery. Bootcamp muze vest kdokoli z tymu, treba
  // majitel nez se najde BC trener, a zakladat mu kvuli tomu profil trenera
  // nedava smysl. Seznam je kratky, takze vsichni aktivni staci.
  const { data: hrData } = useApiData("/api/hr-list-people?status=aktivni");

  const [otevreny, setOtevreny] = useState(null);   // id běhu v překryvu
  const [novy, setNovy] = useState(false);
  const [form, setForm] = useState(PRAZDNY);
  const [saving, setSaving] = useState(false);
  const [chyba, setChyba] = useState("");

  const [search, setSearch] = useState("");
  const [filtrStav, setFiltrStav] = useState([]);
  const [filtrTrener, setFiltrTrener] = useState([]);
  const [filtrRok, setFiltrRok] = useState([]);

  const role = getCurrentUser()?.role || "";
  const smiZakladat = ["owner", "manager", "head_coach", "csm"].includes(role);
  // Nabídka lokalit na webu je jen pro ownera (9. 10. 2026). Zastavit prodej
  // lokality je rozhodnutí majitele, server to hlídá taky.
  const jeOwner = role === "owner";

  const behy = useMemo(() => (data && Array.isArray(data.bootcampy) ? data.bootcampy : []), [data]);
  const treneri = useMemo(() => {
    const lide = (hrData && hrData.people) || [];
    return lide
      .filter((p) => p.active !== false)
      .map((p) => ({ id: p.id, jmeno: p.display_name }))
      .filter((t) => t.id && t.jmeno)
      .sort((a, b) => a.jmeno.localeCompare(b.jmeno, "cs"));
  }, [hrData]);

  const roky = useMemo(() => [...new Set(behy.map((b) => String(b.datum_od || "").substring(0, 4)).filter(Boolean))].sort().reverse(), [behy]);
  const treneriVBezich = useMemo(() => [...new Set(behy.map((b) => b.trener_name).filter(Boolean))].sort((a, b) => a.localeCompare(b, "cs")), [behy]);

  const filtrovane = useMemo(() => {
    const q = bezDiakritiky(search).trim();
    return behy.filter((b) => {
      if (q && !bezDiakritiky(`${b.nazev} ${b.adresa} ${b.misto} ${b.trener_name}`).includes(q)) return false;
      if (filtrStav.length && !filtrStav.includes(b.stav)) return false;
      if (filtrTrener.length && !filtrTrener.includes(b.trener_name)) return false;
      if (filtrRok.length && !filtrRok.includes(String(b.datum_od || "").substring(0, 4))) return false;
      return true;
    });
  }, [behy, search, filtrStav, filtrTrener, filtrRok]);

  const pocetFiltru = [filtrStav, filtrTrener, filtrRok].filter((f) => f.length > 0).length + (search ? 1 : 0);
  const resetFiltru = () => { setSearch(""); setFiltrStav([]); setFiltrTrener([]); setFiltrRok([]); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function uloz() {
    setChyba("");
    if (!form.nazev.trim()) { setChyba("Doplňte název lokality."); return; }
    if (!form.datum_od) { setChyba("Doplňte datum od."); return; }
    if (!form.bez_konce && !form.datum_do) { setChyba("Doplňte datum do, nebo zapněte Bez konce."); return; }
    if (!form.rozvrh.length) { setChyba("Vyberte aspoň jeden den v rozvrhu."); return; }
    setSaving(true);
    try {
      const r = await apiFetch("/api/bootcampy", {
        method: "POST",
        body: JSON.stringify({
          akce: "ulozit-bootcamp",
          fields: { ...form, datum_do: form.bez_konce ? "" : form.datum_do },
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
      setNovy(false);
      setForm(PRAZDNY);
      refetch();
      if (j.id) setOtevreny(String(j.id));
    } catch (e) {
      setChyba(e.message);
    } finally {
      setSaving(false);
    }
  }

  // Celá stránka: ?beh=ID. Kreslí se jako běžný obsah pod lištou se záložkami.
  if (behParam) {
    return (
      <BootcampDetail
        id={behParam}
        fullPage
        onChanged={refetch}
        onClose={() => {
          try { if (window.opener) window.close(); } catch (_) { /* okno neotevřela aplikace */ }
          setSearchParams((sp) => { sp.delete("beh"); return sp; });
        }}
      />
    );
  }

  return (
    <Stranka>
      <GradientHeader
        title="Lokality"
        icon={Flame}
        loading={loading}
        napoveda="Lokality bootcampů. Jedna lokalita je jedno místo se svým rozvrhem, lekcemi a klientkami. Cvičí se celoročně, takže datum do zůstává prázdné. Novou založíš plusem v hlavičce."
        akce={[
          ...(smiZakladat ? [{
            klic: "novy",
            ikona: novy ? X : Plus,
            popis: novy ? "Zavřít formulář" : "Nová lokalita",
            onClick: () => setNovy((v) => !v),
          }] : []),
          {
            klic: "archiv",
            ikona: sArchivem ? ArchiveRestore : Archive,
            popis: sArchivem ? "Skrýt archivované" : "Zobrazit i archivované",
            onClick: () => setSArchivem((v) => !v),
          },
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: loading || refreshing },
        ]}
      />

      {jeOwner && behy.length > 0 && (
        <NabidkaNaWebu
          behy={behy}
          hookNastaven={data?.webHookNastaven === true}
          onZmena={refetch}
        />
      )}

      {novy && (
        <Card lista title="Nová lokalita">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Pole label="Název lokality" hint="Krátce, tak jak ji ženy znají. Jde i do SMS a do klientské aplikace.">
              <input className="field" value={form.nazev} onChange={(e) => set("nazev", e.target.value)} placeholder="Kbely" />
            </Pole>
            <Pole label="Adresa" hint="Kde přesně se sraz koná.">
              <input className="field" value={form.adresa} onChange={(e) => set("adresa", e.target.value)} placeholder="Toužimská 732, Praha 9" />
            </Pole>
            <Pole label="Trenér">
              <select className="field" value={form.trener_id} onChange={(e) => set("trener_id", e.target.value)}>
                <option value="">Nevybráno</option>
                {treneri.map((t) => <option key={t.id} value={t.id}>{t.jmeno}</option>)}
              </select>
            </Pole>
            <Pole label="Od"><input type="date" className="field" value={form.datum_od} onChange={(e) => set("datum_od", e.target.value)} /></Pole>
            <PoleDatumDo
              hodnota={form.datum_do}
              bezKonce={form.bez_konce}
              onZmena={(v) => set("datum_do", v)}
              onPrepni={(v) => setForm((f) => ({ ...f, bez_konce: v, datum_do: v ? "" : f.datum_do }))}
            />
            <Pole label="Přihlášky do"><input type="date" className="field" value={form.prihlasky_do} onChange={(e) => set("prihlasky_do", e.target.value)} /></Pole>
            <Pole label="Prodej od" hint="Od tohohle data se do běhu párují faktury. Prázdné znamená 120 dní před začátkem.">
              <input type="date" className="field" value={form.prodej_od} onChange={(e) => set("prodej_od", e.target.value)} />
            </Pole>
            <Pole label="Kapacita lekce"><input type="number" className="field" value={form.kapacita} onChange={(e) => set("kapacita", e.target.value)} /></Pole>
            <Pole label="Storno lhůta (hodin)"><input type="number" className="field" value={form.storno_hodin} onChange={(e) => set("storno_hodin", e.target.value)} /></Pole>
            <Pole label="Vzor položky faktury" hint="Podřetězec, podle kterého se poznají faktury tohohle běhu.">
              <input className="field" value={form.fapi_pattern} onChange={(e) => set("fapi_pattern", e.target.value)} placeholder="Bootcamp:" />
            </Pole>
            <Pole label="Ceny (text pro přehled)">
              <input className="field" value={form.cena_text} onChange={(e) => set("cena_text", e.target.value)} />
            </Pole>
            <Pole label="Platnost permanentky (dnů)" hint="Od data platby. Dny pauzy se přičtou navíc. 0 znamená bez omezení.">
              <input type="number" className="field" value={form.platnost_dnu} onChange={(e) => set("platnost_dnu", e.target.value)} />
            </Pole>
            <Pole label="ID prodejního formuláře FAPI" hint="Podle něj aplikace nabídne dokoupení vstupů právě do téhle lokality.">
              <input className="field" value={form.fapi_form_id} onChange={(e) => set("fapi_form_id", e.target.value)} placeholder="bb7d5690-9791-40ef-81a3-5f4848ad5b89" />
            </Pole>
            <Pole label="Poznámka" hint="Interní, klientky ji nevidí.">
              <input className="field" value={form.poznamka} onChange={(e) => set("poznamka", e.target.value)} placeholder="Od listopadu do března se cvičí v hale" />
            </Pole>
          </div>

          <div className="mt-4"><RozvrhEditor rozvrh={form.rozvrh} onZmena={(v) => set("rozvrh", v)} /></div>
          <div className="mt-4"><MistaEditor mista={form.mista} onZmena={(v) => set("mista", v)} misto={form.nazev} /></div>
          <div className="mt-4"><PauzyEditor pauzy={form.pauzy} onZmena={(v) => set("pauzy", v)} /></div>

          <div className="mt-4">
            <div className="text-sm font-medium text-slate-700 mb-1.5">
              Tarify <span className="font-normal text-slate-500">(prázdný počet vstupů znamená bez omezení)</span>
            </div>
            <div className="space-y-2">
              {form.tarify.map((t, i) => (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {["nazev", "vzor", "vstupy", "cena"].map((k) => (
                    <input key={k} className="field" value={t[k] == null ? "" : t[k]}
                      placeholder={{ nazev: "Název", vzor: "Vzor v položce faktury", vstupy: "Vstupů", cena: "Cena Kč" }[k]}
                      onChange={(e) => { const n = [...form.tarify]; n[i] = { ...t, [k]: e.target.value }; set("tarify", n); }} />
                  ))}
                </div>
              ))}
              <button type="button" className="text-sm text-brand-700 hover:underline"
                onClick={() => set("tarify", [...form.tarify, { nazev: "", vzor: "", vstupy: "", cena: "" }])}>
                Přidat tarif
              </button>
            </div>
          </div>

          {chyba && <div className="mt-3"><ErrorNote>{chyba}</ErrorNote></div>}

          <div className="mt-4 flex gap-2">
            <button type="button" onClick={uloz} disabled={saving}
              className="btn btn-primary">
              <Save className="w-4 h-4" /> {saving ? "Ukládám..." : "Založit lokalitu"}
            </button>
            <button type="button" onClick={() => { setNovy(false); setChyba(""); }}
              className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm hover:bg-slate-50">
              Zrušit
            </button>
          </div>
        </Card>
      )}

      <Card lista title={`Lokality${data ? ` (${filtrovane.length})` : ""}`}>
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Hledat podle názvu, adresy nebo trenéra (bez háčků)"
              className={`w-full pl-9 pr-3 py-3 text-[15px] border rounded-xl focus:outline-none focus:ring-2 ${
                search ? "border-brand-400 ring-2 ring-brand-100" : "border-slate-200 focus:border-brand-400 focus:ring-brand-100"
              }`}
            />
          </div>
          {pocetFiltru > 0 && (
            <button onClick={resetFiltru} title="Vynulovat všechny filtry"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 transition-colors flex-shrink-0 min-h-[44px] sm:min-h-0">
              <X className="w-4 h-4" /> Reset
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 text-[11px] font-bold rounded-full bg-rose-600 text-white px-1.5">{pocetFiltru}</span>
            </button>
          )}
        </div>

        {loading && behy.length === 0 && <div className="py-12 text-center text-slate-500 text-sm">Načítám lokality…</div>}

        {!loading && behy.length === 0 && (
          <div className="py-12 text-center">
            <Flame className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <div className="text-slate-600 font-medium">Zatím tu není žádná lokalita</div>
            <div className="text-slate-500 text-sm mt-1">Založte první a vygenerujte jí lekce z rozvrhu.</div>
          </div>
        )}

        {behy.length > 0 && (
          <div className="overflow-x-auto">
            {/* 23.8.2026: o stupeň větší písmo a vzdušnější řádky, seznam se
                čte na dálku a bude ho přibývat. */}
            <table className="w-full text-[15px]">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="px-2 py-2.5 font-medium">Lokalita</th>
                  <th className="px-2 py-2.5 font-medium">
                    <MultiSelectFilter
                      title="Filtrovat podle stavu lokality"
                      selected={filtrStav}
                      onChange={setFiltrStav}
                      options={Object.entries(STAV_META).map(([value, m]) => ({ value, label: m.label }))}
                    />
                  </th>
                  <th className="px-2 py-2.5 font-medium hidden md:table-cell">
                    <MultiSelectFilter
                      title="Filtrovat podle roku"
                      selected={filtrRok}
                      onChange={setFiltrRok}
                      options={roky.map((r) => ({ value: r, label: r }))}
                      placeholder="Období"
                    />
                  </th>
                  <th className="px-2 py-2.5 font-medium hidden lg:table-cell">Adresa</th>
                  <th className="px-2 py-2.5 font-medium hidden lg:table-cell">
                    <MultiSelectFilter
                      title="Filtrovat podle trenéra"
                      selected={filtrTrener}
                      onChange={setFiltrTrener}
                      options={treneriVBezich.map((t) => ({ value: t, label: t }))}
                      placeholder="Trenér"
                    />
                  </th>
                  <th className="px-2 py-2.5 font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      Klientky
                      <InfoTip text="Zelená fajfka: zaplaceno. Červený křížek: faktura po splatnosti a neuhrazená. Přesýpací hodiny: čeká na úhradu, faktura zatím běží. Nulové počty se neukazují." />
                    </span>
                  </th>
                  <th className="px-2 py-2.5 font-medium hidden sm:table-cell">Lekcí</th>
                  <th className="px-2 py-3.5"></th>
                </tr>
              </thead>
              <tbody>
                {filtrovane.map((b) => (
                  <tr key={b.id}
                    onClick={() => setOtevreny(String(b.id))}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors">
                    <td className="px-2 py-3.5">
                      <div className="font-semibold text-slate-900 text-[15.5px]">{b.nazev}</div>
                      {/* 3. 11. 2026: na telefonu jen ulice v pilulce na jeden řádek, celá adresa je v title. */}
                      <div className="mt-1 lg:hidden">
                        <span title={b.adresa || b.misto || ""} className="inline-flex max-w-[11rem] items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600">
                          <MapPin className="w-3 h-3 shrink-0" />
                          <span className="truncate">{String(b.adresa || b.misto || "—").split(",")[0]}</span>
                        </span>
                      </div>
                    </td>
                    <td className="px-2 py-3.5"><StavBadge stav={b.stav} /></td>
                    <td className="px-2 py-3.5 hidden md:table-cell whitespace-nowrap text-slate-600">
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                        {b.datum_do
                          ? `${den(b.datum_od)} až ${den(b.datum_do)}`
                          : <>od {den(b.datum_od)} <Nekonecno className="w-3.5 h-3.5 text-brand inline" aria-label="bez konce" /></>}
                      </span>
                    </td>
                    <td className="px-2 py-3.5 hidden lg:table-cell text-slate-600">{b.adresa || "—"}</td>
                    <td className="px-2 py-3.5 hidden lg:table-cell text-slate-600">{b.trener_name || "—"}</td>
                    <td className="px-2 py-3.5 whitespace-nowrap">
                      {/* 16.9.2026: "15 z 15" samo o sobě nikomu nic neřeklo.
                          Teď tři čísla se stavem, nulové se neukazují, ať řádek
                          nekřičí u lokality, kde je všechno zaplacené. */}
                      <Platby
                        zaplaceno={b.pocet_zaplacenych}
                        celkem={b.pocet_ucastnic}
                        poSplatnosti={b.pocet_po_splatnosti || 0}
                      />
                    </td>
                    <td className="px-2 py-3.5 hidden sm:table-cell tabular-nums text-slate-600">{b.pocet_lekci}</td>
                    <td className="px-2 py-3.5 text-right"><ChevronRight className="w-4 h-4 text-slate-300 inline" /></td>
                  </tr>
                ))}
                {filtrovane.length === 0 && behy.length > 0 && (
                  <tr><td colSpan={8} className="py-10 text-center text-slate-500 text-sm">Filtrům neodpovídá žádná lokalita.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {otevreny && (
        <BootcampDetail
          id={otevreny}
          onClose={() => setOtevreny(null)}
          onChanged={refetch}
        />
      )}
    </Stranka>
  );
}
